import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import { createI18n } from 'vue-i18n';
import { Locale } from 'vant';
import apiMsgKeysFlatZhCn from './api-msg-keys-flat.zh-cn.json';

/** 将 `a.b.c` 扁平键转为 vue-i18n 可解析的嵌套 messages */
function nestDotKeysToMessages(
  flat: Record<string, string>,
): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const [fullKey, value] of Object.entries(flat)) {
    const parts = fullKey.split('.');
    let cur: Record<string, unknown> = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      const next = cur[p];
      if (typeof next !== 'object' || next === null || Array.isArray(next)) {
        cur[p] = {};
      }
      cur = cur[p] as Record<string, unknown>;
    }
    cur[parts[parts.length - 1]] = value;
  }
  return root;
}

function normalizeLanguageCode(language: string): string {
  const l = language.toLowerCase();
  /* 存储值为 zh-CN / en-US / sp-ES；sp-ES 中含大写 ES，不能用 includes('es') 误判，需先小写化 */
  if (l.includes('zh')) {
    return 'zh-CN';
  }
  if (l.includes('es')) {
    return 'sp-ES';
  }
  if (l.includes('en')) {
    return 'en-US';
  }
  return 'en-US';
}

function readStoredLanguageRaw(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return (
    localStorage.getItem('IMI18n') ??
    sessionStorage.getItem('IMI18n') ??
    undefined
  );
}

export const localLanguage = () => {
  const raw =
    readStoredLanguageRaw() ??
    (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
  return normalizeLanguageCode(raw);
};

export function loadLanguages() {
  const context = import.meta.glob('./langs/*.ts', { eager: true });
  const languages: any = {};
  const langs = Object.keys(context);
  const apiMsgNested = nestDotKeysToMessages(
    apiMsgKeysFlatZhCn as Record<string, string>,
  );
  for (const key of langs) {
    if (key === './index.ts') return;
    const lang = (context[key] as any).lang;
    const name = key.replace(/(\.\/langs\/|\.ts)/g, '');
    languages[name] =
      name === 'zh-CN' ? { ...lang, ...apiMsgNested } : lang;
  }
  return languages;
}

const initialLocale = localLanguage();

export const i18n = createI18n({
  locale: initialLocale,
  fallbackLocale: 'en-US',
  messages: loadLanguages(),
  legacy: false,
  /** 模板中 $t / $i18n 等需在非 useI18n 组件内可用（如会话 MessageItem） */
  globalInjection: true,
});

export function i18nt(key: string) {
  // @ts-ignore
  return i18n.global.t(key);
}

const toDayjsLocale = (locale: string) => {
  if (locale === 'zh-CN') return 'zh-cn';
  if (locale === 'sp-ES') return 'es';
  return 'en';
};

function applyI18nLocale(locale: string) {
  (i18n.global.locale as any).value = locale;
  dayjs.locale(toDayjsLocale(locale));
  Locale.use(locale, (i18n.global.messages as any)[locale]?.vant);
}

/** 接口请求头 lang：与后端约定 ZH / EN / ES */
export type ApiLangCode = 'ZH' | 'EN' | 'ES';

export function getApiLang(): ApiLangCode {
  const loc =
    typeof (i18n.global.locale as any)?.value === 'string'
      ? (i18n.global.locale as any).value
      : String(i18n.global.locale);
  if (loc === 'zh-CN') return 'ZH';
  if (loc === 'sp-ES') return 'ES';
  return 'EN';
}

dayjs.locale(toDayjsLocale(initialLocale));
Locale.use(initialLocale, (i18n.global.messages as any)[initialLocale]?.vant);

export function setLanguage(locale: string) {
  applyI18nLocale(locale);
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem('IMI18n', locale);
    sessionStorage.setItem('IMI18n', locale);
  } catch {
    // 无痕模式、存储配额等
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key !== 'IMI18n' || e.newValue == null) return;
    const loc = normalizeLanguageCode(e.newValue);
    applyI18nLocale(loc);
    try {
      sessionStorage.setItem('IMI18n', loc);
    } catch {
      /* ignore */
    }
  });
}
